import {
  TabItem,
  DisplayFieldItem,
  FilterItem,
  LeadStatusFilterItem,
  ExpandedViewItem,
  DedupeStatusFilterItem,
} from '../LeadManagement.types';
import { STATUS } from '../constants';

export interface ColumnConfig {
  id: string;
  label: string;
  field: string;
  sortable?: boolean;
  width?: string | number;
  dropDown?: Array<{
    label: string;
    value: string;
  }>;
}

export interface FilterConfig {
  id: string;
  label: string;
  field: string;
  type: 'text' | 'select' | 'date' | 'dateRange' | 'slider';
  options?: Array<{
    label: string;
    value: string;
  }>;
  multiple?: boolean; // Support for multi-select dropdowns
  min?: number;
  max?: number;
  maxCap?: number;
  apiEndpoint?: string;
  apiConfig?: {
    method?: 'GET' | 'POST';
    baseUrl?: string;
    headers?: Record<string, string>;
    body?: Record<string, any>;
    responseKey?:
      | 'cities'
      | 'states'
      | 'associatedProtocols'
      | 'qualifiedProtocols'
      | 'sites'
      | 'ethnicity'
      | 'userIds'
      | 'patientReferralStatuses'; // Key to extract from API response
  };
}

export interface LeadStatusFilter {
  label: string;
  value: string;
  color?: string;
  bgColor?: string;
  daysLowerLimit?: number;
  daysUpperLimit?: number;
  rowBgColor?: string;
}

export interface DedupeStatusFilter {
  label: string;
  value: string;
  color?: string;
  bgColor?: string;
}
export interface QuickFilters {
  label: string;
  value: string;
  color?: string;
  bgColor?: string;
}

export interface TabConfiguration {
  tabKey: string;
  tabName: string;
  noResultsText?: string;
  viewDetails?: {
    href: string;
    text: string;
  };
  sendToCC?: string;
  columns: ColumnConfig[];
  filters: FilterConfig[];
  leadTypeText?: string;
  leadStatusFilters: LeadStatusFilter[];
  dedupeStatusText?: string;
  dedupeStatusFilters: DedupeStatusFilter[];
  expandedFields: {
    personal: Array<{ key: string; label: string }>;
    demographics: Array<{ key: string; label: string }>;
    caregiverInfo: Array<{ key: string; label: string }>;
    trackingParams: Array<{ key: string; label: string }>;
    dedupAddParams: Array<{ key: string; label: string }>;
  };
  quickFilterText?: string;
  quickFilters: QuickFilters[];
}

/**
 * Extract unique site options from referral data
 * @param data Array of referral data
 * @returns Array of site options with label and value
 */
export const extractSiteOptions = (
  data: Array<{ siteName?: string | { siteName: string; siteId: string } }>
): Array<{ label: string; value: string }> => {
  const siteMap = new Map<string, string>();

  data.forEach((item) => {
    if (item.siteName) {
      // Handle both string and object formats
      if (typeof item.siteName === 'string') {
        // If siteName is just a string, use it for both label and value
        siteMap.set(item.siteName, item.siteName);
      } else if (
        typeof item.siteName === 'object' &&
        item.siteName.siteId &&
        item.siteName.siteName
      ) {
        // If siteName is an object with siteId and siteName properties
        const siteId = item.siteName.siteId;
        const siteName = item.siteName.siteName;

        // Use siteId as key to ensure uniqueness
        if (!siteMap.has(siteId)) {
          siteMap.set(siteId, siteName);
        }
      }
    }
  });

  // Convert map to array of options, sorted by label
  return Array.from(siteMap.entries())
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Build column configuration from Sitecore display fields
 */
export const buildColumns = (displayFields: DisplayFieldItem[]): ColumnConfig[] => {
  return displayFields.map((field) => {
    const fieldKey = field.fields.DisplayFieldsKey?.value || field.name;
    const column: ColumnConfig = {
      id: fieldKey,
      label: field.fields.DisplayFieldsLabel?.value || field.name,
      field: fieldKey,
      sortable: field.fields.SortingEnabler?.value || false,
    };

    // Add dropdown configuration for dedupeStatus field
    if (fieldKey === 'dedupeStatus' || fieldKey === 'status') {
      column.dropDown = [
        {
          label: 'Manual Review',
          value: STATUS.MANUAL_REVIEW_REQUIRED,
        },
        {
          label: 'Auto Merge',
          value: STATUS.AUTO_MERGE,
        },
        {
          label: 'New',
          value: STATUS.NEW_RECORD,
        },
        {
          label: 'Returning User',
          value: STATUS.RETURNING_USER,
        },
      ];
    }

    return column;
  });
};

/**
 * API endpoint for LMA filter dropdowns
 */
const LMA_DROPDOWN_API_ENDPOINT = '/LMA/GetLMAFilterDropdownValues';

/**
 * Build filter configuration from Sitecore filter fields
 * @param filterFields Array of filter field items from Sitecore
 * @param genderValues Optional gender values from Sitecore for gender dropdown
 */
export const buildFilters = (
  filterFields: FilterItem[],
  genderValues?: Array<{
    fields?: { DisplayFieldsLabel?: { value: string }; DisplayFieldsKey?: { value: string } };
    id?: string;
  }>,
  sliderMinValue?: number,
  sliderMaxValue?: number,
  sliderMaxCapValue?: number
): FilterConfig[] => {
  return filterFields
    .filter((field) => field.fields.FilterLabel?.value) // Filter out empty labels
    .map((field) => {
      const filterKey = field.fields.FilterKey?.value || field.name;

      // Determine filter type based on field name
      let type: FilterConfig['type'] = 'text';

      if (filterKey.toLowerCase().includes('date') && filterKey.toLowerCase().includes('range')) {
        type = 'dateRange';
      } else if (
        filterKey.toLowerCase().includes('date') ||
        filterKey.toLowerCase().includes('dob')
      ) {
        type = 'date';
      } else if (
        filterKey.toLowerCase().includes('percentage') ||
        filterKey.toLowerCase().includes('match')
      ) {
        type = 'slider';
      } else if (
        filterKey.toLowerCase().includes('status') ||
        filterKey.toLowerCase().includes('gender') ||
        filterKey.toLowerCase().includes('site') ||
        filterKey.toLowerCase().includes('protocol') ||
        filterKey.toLowerCase().includes('city') ||
        filterKey.toLowerCase().includes('state') ||
        filterKey.toLowerCase().includes('ethnicity') ||
        filterKey.toLowerCase().includes('userids')
      ) {
        type = 'select';
      }

      const filter: FilterConfig = {
        id: filterKey,
        label: field.fields.FilterLabel?.value || field.name,
        field: filterKey,
        type,
        // Enable multi-select for dropdown/select type filters
        multiple: type === 'select' ? true : undefined,
        min: type === 'slider' ? sliderMinValue ?? 0 : undefined,
        max: type === 'slider' ? sliderMaxValue ?? 100 : undefined,
        maxCap: type === 'slider' ? sliderMaxCapValue ?? 100 : undefined,
      };

      // Add dropdown options for dedupeStatus filter
      if (filterKey === 'dedupeStatus' || filterKey === 'status') {
        filter.options = [
          {
            label: 'Manual Review',
            value: STATUS.MANUAL_REVIEW_REQUIRED,
          },
          {
            label: 'Auto Merge',
            value: STATUS.AUTO_MERGE,
          },
          {
            label: 'New',
            value: STATUS.NEW_RECORD,
          },
          {
            label: 'Returning_user',
            value: STATUS.RETURNING_USER,
          },
        ];
      }

      // Add dropdown options for gender filter
      if (filterKey === 'gender' || filterKey.toLowerCase().includes('gender')) {
        // Use dynamic gender values from Sitecore if available, otherwise use defaults
        if (genderValues && genderValues.length > 0) {
          filter.options = genderValues
            .map((item) => ({
              label: item.fields?.DisplayFieldsLabel?.value || '',
              value: item.fields?.DisplayFieldsKey?.value || '',
            }))
            .filter((option) => option.label && option.value); // Filter out empty options
        }
        filter.multiple = false;
      }
      // Configure API-based dropdowns for city, state, site, and protocol filters
      // These will fetch options from LMA/GetLMAFilterDropdownValues API
      if (filterKey === 'ethnicity' || filterKey.toLowerCase().includes('ethnicity')) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'ethnicity',
        };
        filter.options = [];
      } else if (filterKey === 'city' || filterKey.toLowerCase().includes('city')) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'cities',
        };
        filter.options = []; // Will be populated from API
      } else if (filterKey === 'state' || filterKey.toLowerCase().includes('state')) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'states',
        };
        filter.options = []; // Will be populated from API
      } else if (
        filterKey === 'associatedProtocol' ||
        filterKey === 'protocol' ||
        filterKey.toLowerCase().includes('associatedprotocol')
      ) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'associatedProtocols',
        };
        filter.options = []; // Will be populated from API
      } else if (
        filterKey === 'qualifiedProtocol' ||
        filterKey.toLowerCase().includes('qualifiedprotocol')
      ) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'qualifiedProtocols',
        };
        filter.options = []; // Will be populated from API
      } else if (filterKey === 'site' || filterKey.toLowerCase().includes('site')) {
        // Site filter uses API instead of static data
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'sites',
        };
        filter.options = []; // Will be populated from API
      } else if (filterKey === 'userIds' || filterKey.toLowerCase().includes('userids')) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'userIds',
        };
        filter.options = [];
      } else if (
        filterKey === 'patientReferralStatus' ||
        filterKey.toLowerCase().includes('patientreferralstatus')
      ) {
        filter.apiEndpoint = LMA_DROPDOWN_API_ENDPOINT;
        filter.apiConfig = {
          method: 'GET',
          responseKey: 'patientReferralStatuses',
        };
        filter.options = [];
      }

      return filter;
    });
};

/**
 * Helper function to convert hex color to rgba with opacity for background
 */
const hexToRgba = (hex: string, alpha = 0.1): string => {
  // Remove # if present
  hex = hex.replace('#', '');

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Build lead status filters configuration from Sitecore
 */
export const buildLeadStatusFilters = (
  statusFilters: LeadStatusFilterItem[]
): LeadStatusFilter[] => {
  return statusFilters.map((filter) => {
    const label = filter.fields.LSFLabel?.value || filter.name;
    const value = filter.fields.LSFKey?.value || filter.name;
    const color = filter.fields.LSFColor?.value || '#255E5E';
    const rowBgColor = filter.fields.rowBgColor?.value;
    const daysLowerLimit = filter.fields.LSFDaysLowerLimit?.value
      ? parseInt(filter.fields.LSFDaysLowerLimit.value, 10)
      : undefined;
    const daysUpperLimit = filter.fields.LSFDaysUpperLimit?.value
      ? parseInt(filter.fields.LSFDaysUpperLimit.value, 10)
      : undefined;

    // Generate a lighter background color from the main color
    const bgColor = hexToRgba(color, 0.1);

    return {
      label,
      value,
      color,
      bgColor,
      daysLowerLimit,
      daysUpperLimit,
      rowBgColor,
    };
  });
};

export const buildDedupeStatusFilters = (
  statusFilters: DedupeStatusFilterItem[]
): DedupeStatusFilter[] => {
  return statusFilters.map((filter) => {
    const label = filter.fields.DisplayFieldsLabel?.value || filter.name;
    const value = filter.fields.DisplayFieldsKey?.value || filter.name;
    const color = filter.fields.DisplayFieldsColor?.value || '#255E5E';

    // Generate a lighter background color from the main color
    const bgColor = hexToRgba(color, 0.1);

    return {
      label,
      value,
      color,
      bgColor,
    };
  });
};

/**
 * Build expanded view fields configuration
 */
export const buildExpandedFields = (tabFields: TabItem['fields']) => {
  // Map function for ExpandedDisplayFields (uses DisplayFieldsKey and DisplayFieldsLabel)
  const mapDisplayFields = (items: DisplayFieldItem[] = []) => {
    return items.map((item) => ({
      key: item.fields.DisplayFieldsKey?.value || '',
      label: item.fields.DisplayFieldsLabel?.value || '',
    }));
  };

  // Map function for other expanded view items (uses ExpandedViewItemKey and ExpandedViewItemLabel)
  const mapExpandedFields = (items: ExpandedViewItem[] = []) => {
    return items.map((item) => ({
      key: item.fields.ExpandedViewItemKey?.value || '',
      label: item.fields.ExpandedViewItemLabel?.value || '',
    }));
  };

  return {
    personal: mapDisplayFields(tabFields.ExpandedDisplayFields || []),
    demographics: mapExpandedFields(tabFields.Demographics || []),
    caregiverInfo: mapExpandedFields(tabFields.CaregiverInfo || []),
    trackingParams: mapExpandedFields(tabFields.TrackingParams || []),
    dedupAddParams: mapExpandedFields(tabFields.DedupAddParams || []),
    auditChanges: mapExpandedFields(tabFields.AuditChanges || []),
  };
};

/**
 * Build complete tab configuration from Sitecore tab item
 * @param tabItem The tab item from Sitecore
 */
export const buildTabConfiguration = (tabItem: TabItem | undefined): TabConfiguration | null => {
  if (!tabItem || !tabItem.fields) {
    return null;
  }

  const fields = tabItem.fields;

  const sliderMinValue = (fields.MatchSliderMinValue as any)?.value
    ? parseInt((fields.MatchSliderMinValue as any).value, 10)
    : 0;
  const sliderMaxValue = (fields.MatchSliderMaxValue as any)?.value
    ? parseInt((fields.MatchSliderMaxValue as any).value, 10)
    : 100;
  const sliderMaxCapValue = (fields.MatchSliderMaxCapValue as any)?.value
    ? parseInt((fields.MatchSliderMaxCapValue as any).value, 10)
    : 150;

  return {
    tabKey: fields.TabKey?.value || '',
    tabName: fields.TabName?.value || '',
    noResultsText: fields.NoResultsText?.value,
    sendToCC: fields.SendToCC?.value,
    viewDetails: fields.ViewDetails?.value as { href: string; text: string } | undefined,
    columns: buildColumns(fields.AvailableDisplayFields || []),
    filters: buildFilters(
      fields.AvailableFilters || [],
      fields.GenderValues,
      sliderMinValue,
      sliderMaxValue,
      sliderMaxCapValue
    ),
    leadTypeText: fields.LeadTypeText?.value || '',
    dedupeStatusText: fields.DedupeStatusText?.value || '',
    leadStatusFilters: buildLeadStatusFilters(fields.LeadStatusFilters || []),
    dedupeStatusFilters: buildDedupeStatusFilters(fields.DedupeStatusFilters || []),
    expandedFields: buildExpandedFields(fields),
    quickFilterText: fields.QuickFilterText?.value || '',
    quickFilters: Array.isArray(fields.QuickFilters)
      ? fields.QuickFilters.filter((item: any) => item && item.fields).map((item: any) => ({
          label: item.fields.FilterLabel?.value || item.fields.DisplayFieldsLabel?.value || '',
          value: item.fields.FilterKey?.value || item.fields.DisplayFieldsKey?.value || '',
          color:
            item.fields.FilterColor?.value || item.fields.DisplayFieldsColor?.value || '#255E5E',
          bgColor: hexToRgba(
            item.fields.FilterColor?.value || item.fields.DisplayFieldsColor?.value || '#255E5E',
            0.1
          ),
        }))
      : [],
  };
};

/**
 * Get tab configuration based on active tab index
 * @param tabIndex The current tab index
 * @param referralTab The referral tab item from Sitecore
 * @param nonReferralTab The non-referral tab item from Sitecore
 * @param processedTab The processed tab item from Sitecore
 */
export const getTabConfigurationByIndex = (
  tabIndex: number,
  referralTab: TabItem | undefined,
  nonReferralTab: TabItem | undefined,
  processedTab: TabItem | undefined,
  flaggedRecordsTab: TabItem | undefined
): TabConfiguration | null => {
  switch (tabIndex) {
    case 0:
      return buildTabConfiguration(referralTab);
    case 1:
      return buildTabConfiguration(nonReferralTab);
    case 2:
      return buildTabConfiguration(processedTab);
    case 3:
      return buildTabConfiguration(flaggedRecordsTab);
    default:
      return null;
  }
};
